import React, { useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useDidUpdate } from "hooks";
import { debounce } from "lodash";
import { AccountSettings, MeasureType } from "store/types";

import { MeasuresSelect } from "./MeasuresSelect";

type Rules = {
  id: number;
} & AccountSettings["validation_settings"]["rules"][number];

type State = {
  rules: Rules[];
  message: string | null;
};
interface Props {
  rules: Rules[];
  message: string | null;
  updating: boolean;
  updateSettings: (data: Partial<AccountSettings>) => Promise<AccountSettings>;
}

type Threshold = {
  value: string;
  label: string;
  description: string;
  group: "Relative options" | "Absolute options";
};

const thresholds: Threshold[] = [
  {
    value: "relative",
    label: "Relative delta",
    description: "% change from the prior value",
    group: "Relative options"
  },
  {
    value: "absolute",
    label: "Relative absolute",
    description: "Amount change from the prior value",
    group: "Relative options"
  },
  {
    value: "both",
    label: "Relative delta and absolute",
    description: "Both relative change thresholds",
    group: "Relative options"
  },
  {
    value: "absolute_higher",
    label: "Absolute higher than",
    description: "Resulting value above a reference",
    group: "Absolute options"
  },
  {
    value: "absolute_lower",
    label: "Absolute lower than",
    description: "Resulting value below a reference",
    group: "Absolute options"
  }
];

const isAbsoluteThreshold = (threshold: string | null) =>
  threshold === "absolute_higher" || threshold === "absolute_lower";

type ValueField = "absolute_value" | "relative_value" | "reference_value";

// The value field(s) each threshold uses. On threshold change we keep these and clear the rest,
// so switching between thresholds that share a field (e.g. absolute_higher ↔ absolute_lower) preserves its value.
const THRESHOLD_VALUE_FIELDS: Record<string, ValueField[]> = {
  relative: ["relative_value"],
  absolute: ["absolute_value"],
  both: ["absolute_value", "relative_value"],
  absolute_higher: ["reference_value"],
  absolute_lower: ["reference_value"]
};

const MAX_INPUTS = 4;
const GAP_BETWEEN_INPUTS = 10;

export const MeasureDataValidation: React.FC<Props> = ({ rules = [], message = null, updating, updateSettings }) => {
  const [state, setState] = useState<State>({ rules, message });

  const addValidator = () => {
    const maxId = state.rules.reduce((max, validator) => Math.max(max, Number(validator.id)), 0);
    setState((prev) => ({
      ...prev,
      rules: [
        ...prev.rules,
        {
          id: maxId + 1,
          measure_type: null,
          key: null,
          threshold: null,
          absolute_value: null,
          relative_value: null,
          reference_value: null
        }
      ]
    }));
  };

  const removeValidator = (id: number) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.filter((validator) => validator.id !== id)
    }));
  };

  const handlePickMeasure = ({
    id,
    option
  }: {
    id: number;
    option: { measure_type: MeasureType; key: string } | null;
  }) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((item) => (item.id === id ? { ...item, ...option } : item))
    }));
  };

  const handleChangeThreshold = ({ id, value }: { id: number; value: string | null }) => {
    const usedFields = (value && THRESHOLD_VALUE_FIELDS[value]) || [];
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          threshold: value,
          absolute_value: usedFields.includes("absolute_value") ? item.absolute_value : null,
          relative_value: usedFields.includes("relative_value") ? item.relative_value : null,
          reference_value: usedFields.includes("reference_value") ? item.reference_value : null
        };
      })
    }));
  };

  const handleChangeNumbers = ({
    id,
    key,
    value
  }: {
    id: number;
    key: "absolute_value" | "relative_value" | "reference_value";
    value: number | null;
  }) => {
    setState((prev) => ({
      ...prev,
      rules: prev.rules.map((item) => {
        if (item.id !== id) return item;
        return { ...item, [key]: value };
      })
    }));
  };

  const inputWidth = `calc((100% - ${GAP_BETWEEN_INPUTS * (MAX_INPUTS - 1)}px) / ${MAX_INPUTS})`;

  const debounceSendData = useMemo(
    () =>
      debounce((state: State) => {
        const dataToSend = {
          validation_settings: {
            ...state,
            rules: state.rules
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .map(({ id, ...rest }) => {
                return rest;
              })
              .filter((rule) => rule.measure_type !== null && rule.key !== null && rule.threshold !== null)
          }
        };
        updateSettings(dataToSend);
      }, 1000),
    []
  );

  useDidUpdate(() => {
    debounceSendData(state);
  }, [state]);

  return (
    <Stack
      sx={{
        gap: "16px",
        bgcolor: "#FFFFFF",
        p: "16px",
        boxShadow: "sm",
        borderRadius: "8px",
        color: "#071F35"
      }}
    >
      <Stack>
        <Typography variant="h6">Validation KPIs</Typography>
        <Typography variant="body1">
          Flag risky value entries before they are saved. Up to three validators, each on one parameter. A validator
          checks either the change from the prior value or the resulting value itself.
        </Typography>
      </Stack>
      <Stack sx={{ gap: "20px" }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          Validators
        </Typography>
        {state.rules.map((rule, index) => (
          <Stack
            key={index}
            sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: "10px" }}
          >
            <Stack sx={{ flexGrow: 1, flexDirection: "row", gap: "10px", alignItems: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {index + 1}.
              </Typography>
              <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "center", flexGrow: 1 }}>
                <MeasuresSelect
                  sx={{ width: inputWidth }}
                  disabled={updating}
                  value={rule?.measure_type && rule?.key ? { measure_type: rule.measure_type, key: rule.key } : null}
                  onChange={(value) => handlePickMeasure({ id: rule.id, option: value })}
                />
                {rule.measure_type && rule.key && (
                  <Autocomplete<Threshold, false, true>
                    sx={{ width: inputWidth }}
                    disabled={updating}
                    options={thresholds}
                    disableClearable
                    groupBy={(option) => option.group}
                    value={thresholds.find((t) => t.value === rule.threshold) ?? (null as unknown as Threshold)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => <TextField {...params} label="Threshold" />}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        key={option.value}
                        sx={{ flexDirection: "column", alignItems: "flex-start !important", gap: "2px" }}
                      >
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="caption" sx={{ color: "#5D7782" }}>
                          {option.description}
                        </Typography>
                      </Box>
                    )}
                    onChange={(_, value) => {
                      handleChangeThreshold({ id: rule.id, value: value?.value ?? null });
                    }}
                  />
                )}
                {(rule.threshold === "absolute" || rule.threshold === "both") && (
                  <NumberFormat
                    sx={{ width: inputWidth }}
                    label="Absolute Value"
                    thousandSeparator
                    decimalScale={2}
                    disabled={updating}
                    allowNegative={false}
                    customInput={TextField}
                    value={rule.absolute_value}
                    onValueChange={({ floatValue }) => {
                      handleChangeNumbers({
                        id: rule.id,
                        key: "absolute_value",
                        value: floatValue || null
                      });
                    }}
                  />
                )}
                {(rule.threshold === "relative" || rule.threshold === "both") && (
                  <NumberFormat
                    sx={{ width: inputWidth }}
                    label="Relative Value"
                    decimalScale={2}
                    suffix="%"
                    disabled={updating}
                    max={100}
                    allowNegative={false}
                    customInput={TextField}
                    value={rule.relative_value}
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      if (!floatValue) return true;
                      return floatValue >= 0 && floatValue <= 100;
                    }}
                    onValueChange={({ floatValue }) => {
                      handleChangeNumbers({
                        id: rule.id,
                        key: "relative_value",
                        value: floatValue || null
                      });
                    }}
                  />
                )}
                {isAbsoluteThreshold(rule.threshold) && (
                  <NumberFormat
                    sx={{ width: inputWidth }}
                    label="Reference Value"
                    thousandSeparator
                    decimalScale={2}
                    disabled={updating}
                    customInput={TextField}
                    value={rule.reference_value}
                    onValueChange={({ floatValue }) => {
                      handleChangeNumbers({
                        id: rule.id,
                        key: "reference_value",
                        value: floatValue ?? null
                      });
                    }}
                  />
                )}
              </Stack>
            </Stack>
            <Box sx={{ width: 32, height: 32 }}>
              {state.rules.length > 1 && (
                <IconButton size="sm" onClick={() => removeValidator(rule.id)}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Stack>
        ))}
        {state.rules.length < 3 ? (
          <Button sx={{ color: "#26A6AE", alignSelf: "flex-start" }} onClick={addValidator}>
            Add validator
          </Button>
        ) : (
          <Typography variant="caption" sx={{ color: "#5D7782" }}>
            Three validators in use. Remove one to add another.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
